import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { saveReport, getReport } from '../services/firestore';
import { updateCalculatedValues } from '../utils/reportCalculations';

// ───────────────────────────────────────────────────────────────────────────────
// Context helpers
// ───────────────────────────────────────────────────────────────────────────────

const ReportContext = createContext({
  loading: true,
  reportData: {},
  stepStatuses: {},
  setReportData: () => {},
  saveStep: () => {},
  setReportId: () => {},
});

export const useReport = () => useContext(ReportContext);

// ───────────────────────────────────────────────────────────────────────────────
// Reducer
// ───────────────────────────────────────────────────────────────────────────────

const reportReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_REPORT_DATA': {
      const newData = { ...state.reportData, ...action.payload };
      const changed = Object.keys(action.payload).some(
        (k) => state.reportData[k] !== action.payload[k]
      );
      return changed ? { ...state, reportData: newData } : state;
    }

    case 'UPDATE_REPORT_STATE':
      return {
        ...state,
        reportData: action.payload.reportData,
        stepStatuses: action.payload.stepStatuses,
      };

    case 'SET_STEP_STATUSES':
      return {
        ...state,
        stepStatuses: action.payload,
      };

    default:
      return state;
  }
};

// ───────────────────────────────────────────────────────────────────────────────
// Provider
// ───────────────────────────────────────────────────────────────────────────────

export const ReportProvider = ({ children }) => {
  const { currentUser } = useAuth();

  const [reportId, setReportId] = useState(null);
  const [loading, setLoading] = useState(true);

  const initialState = useMemo(
    () => ({
      loading: true,
      reportData: {},
      stepStatuses: {},
    }),
    []
  );

  const [state, dispatch] = useReducer(reportReducer, initialState);

  // Create a ref to hold the latest state to avoid dependency cycles in callbacks
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ──────────────────────────────────────────────
  // Fetch Firestore doc whenever reportId changes
  // ──────────────────────────────────────────────
  useEffect(() => {
    if (!reportId) {
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);

    getReport(reportId)
      .then((data) => {
        if (!alive) return;
        if (data) {
          // When loading a report, its data and step statuses are loaded
          // together, so we can update both in one operation.
          const { stepStatuses, ...reportData } = data;
          dispatch({
            type: 'UPDATE_REPORT_STATE',
            payload: {
              reportData,
              stepStatuses: stepStatuses || {},
            },
          });
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [reportId]);

  // ──────────────────────────────────────────────
  // Mutators
  // ──────────────────────────────────────────────

  const setReportData = useCallback(
    (data, runCalculations = false) => {
      //console.log(`[Context] setReportData called. runCalculations: ${runCalculations}`, data);
      if (runCalculations) {
        // Use the state ref to get the most recent data without creating a dependency
        const fullData = { ...stateRef.current.reportData, ...data };
        //console.log('[Context] setReportData: Running calculations with fullData:', fullData);
        dispatch({ type: 'SET_LOADING', payload: true });
        updateCalculatedValues(fullData, stateRef.current.reportData)
          .then((calculatedFields) => {
            //console.log('[Context] setReportData: Calculations complete. Calculated fields:', calculatedFields);
            // The new state should be the combination of the data used for calculation and the results
            const newData = { ...fullData, ...calculatedFields };
            dispatch({
              type: 'SET_REPORT_DATA',
              payload: newData,
            });
          })
          .catch((err) => console.error('Error updating calculated values:', err))
          .finally(() => dispatch({ type: 'SET_LOADING', payload: false }));
      } else {
        dispatch({ type: 'SET_REPORT_DATA', payload: data });
      }
    },
    [] // Empty dependency array ensures the function is stable
  );

  const saveStep = useCallback(
    async (formValues, activeStep, reportConfig, reportType) => {
      //console.log('[Context] saveStep: Initiated.', { formValues, activeStep, reportType });
      const currentState = stateRef.current;
      // Ensure the latest form values are merged with the current state for calculations.
      const dataToCalculate = { ...currentState.reportData, ...formValues };
      if (!dataToCalculate) {
        console.log('[Context] saveStep: No data to calculate. Aborting.');
        return;
      }

      //console.log('[Context] saveStep: Running final calculations before save.', dataToCalculate);
      // Always run calculations before saving a step to guarantee accuracy.
      // Pass the *original* state to compare against, ensuring changes are detected.
      const calculatedFields = await updateCalculatedValues(dataToCalculate, currentState.reportData);
      const dataWithCalculations = { ...dataToCalculate, ...calculatedFields };
      //console.log('[Context] saveStep: Final data with calculations.', dataWithCalculations);

      const currentStepSchema = reportConfig.steps[activeStep]?.validationSchema;
      let status = 'in-progress';

      if (currentStepSchema) {
        try {
          await currentStepSchema.validate(dataWithCalculations);
          status = 'completed';
        } catch (err) {
          status = 'error';
        }
      }

      const newStepStatuses = { ...currentState.stepStatuses, [activeStep]: status };
      const { userId: _discard, ...reportBody } = dataWithCalculations;

      const payload = {
        ...reportBody,
        userId: currentUser?.uid,
        stepStatuses: newStepStatuses,
        reportType,
        updatedAt: Timestamp.now(),
      };

      try {
        //console.log('[Context] saveStep: Saving to Firestore. Payload:', payload);
        const id = await saveReport(payload, reportId);
        //console.log(`[Context] saveStep: Save successful. Report ID: ${id}. Updating local state.`);
        dispatch({
          type: 'UPDATE_REPORT_STATE',
          payload: {
            reportData: { ...dataWithCalculations, id },
            stepStatuses: newStepStatuses,
          },
        });
        return id;
      } catch (err) {
        console.error('Failed to save report step:', err);
        throw err;
      }
    },
    [currentUser, reportId] // Stable dependencies
  );

  const value = useMemo(
    () => ({
      loading,
      reportData: state.reportData,
      stepStatuses: state.stepStatuses,
      setReportData,
      saveStep,
      setReportId,
    }),
    [loading, state.reportData, state.stepStatuses, setReportData, saveStep]
  );

  return (
    <ReportContext.Provider value={value}>{children}</ReportContext.Provider>
  );
}

export default ReportProvider;